import { cn } from '@/lib/cn';

interface SkeletonProps {
  className?: string;
  /** Width preset */
  width?: string;
  /** Height preset */
  height?: string;
}

/**
 * Animated shimmer skeleton for loading states.
 */
export function Skeleton({ className, width, height }: SkeletonProps) {
  return (
    <div
      className={cn('rounded-lg skeleton-shimmer', className)}
      style={{ width, height }}
    />
  );
}

/**
 * Skeleton for a text line.
 */
export function SkeletonText({
  lines = 3,
  className,
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={cn('space-y-2.5', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className="h-3.5"
          width={i === lines - 1 ? '60%' : '100%'}
        />
      ))}
    </div>
  );
}

/**
 * Skeleton for a card (used in report listing loading state).
 */
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'bg-white rounded-xl border border-slate-200/80 p-5 space-y-3',
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <Skeleton className="w-9 h-9 rounded-lg" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-3.5 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-2/3" />
    </div>
  );
}

/**
 * Skeleton for a chart area.
 */
export function SkeletonChart({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'bg-white rounded-xl border border-slate-200/80 p-6',
        className,
      )}
    >
      <div className="flex items-center justify-between mb-6">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-8 w-24 rounded-lg" />
      </div>
      <div className="flex items-end gap-2 h-48">
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton
            key={i}
            className="flex-1 rounded-t-md"
            height={`${Math.random() * 60 + 30}%`}
          />
        ))}
      </div>
    </div>
  );
}
